/**
 * @fileoverview Zod schema for the ai_prompt entity. Stores reusable AI prompt
 * templates with configurable model settings and display ordering.
 */

import { z } from 'zod';
import { archivedAtSchema, commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { ID_PATTERNS } from './types.js';

/**
 * Maximum output tokens for the editor's AI actions (Improve Phrasing, Format
 * with Markdown, etc.). The backend passes it to the model; the editor warns
 * when the text sent is likely to produce a result longer than this.
 */
export const AI_ACTION_MAX_OUTPUT_TOKENS = 20_000;

export const AiPromptSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.AI_PROMPT, 'ID must be in ap-prefixed nanoid7 format'),
  title: commonValidators.requiredString('Title', 255),
  content: z.string().min(1, 'Content is required'),
  display_order: commonValidators.displayOrder(),
  prompt_key: commonValidators.requiredString('Prompt Key', 100),
  model: commonValidators.optionalString(255),
  temperature: z.number()
    .min(0, 'Temperature must be at least 0')
    .max(2, 'Temperature must be at most 2')
    .optional()
    .nullable(),
  max_tokens: z.number()
    .int('Max tokens must be an integer')
    .positive('Max tokens must be positive')
    .optional()
    .nullable(),
  context: z.string().optional().nullable(),
  metadata: commonValidators.jsonField('Metadata'),
  archived_at: archivedAtSchema,
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(AiPromptSchema);

export const AiPromptCreateSchema = CreateSchema;
export const AiPromptUpdateSchema = UpdateSchema;

export type AiPrompt = z.infer<typeof AiPromptSchema>;
export type AiPromptCreate = z.infer<typeof AiPromptCreateSchema>;
export type AiPromptUpdate = z.infer<typeof AiPromptUpdateSchema>;

export const AI_PROMPT_FIELDS = getSchemaFields(AiPromptSchema);
